import type { ReactNode } from 'react';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import { useOfficialExam } from '@/modules/official-exam/presentation/hooks/use-official-exam';
import {
  outcomeOf,
  scoreGabarito,
  type QuestionOutcome,
} from '@/modules/gabarito/domain/entities/gabarito.entity';
import { gabaritoMaterial } from '@/modules/tenant/domain/entities/materials';
import type { AnswerKeyEntry } from '@/modules/diagnostico/domain/entities/answer-key.entity';
import type { Answers } from '@/modules/simulado/domain/entities/answer.entity';
import {
  GABARITO_SUBMISSION_NOTE,
  SendGabaritoAction,
  useGabaritoSubmission,
} from '@/modules/gabarito/presentation/components/SendGabaritoAction';
import { GabaritoDownloadLink } from '@/modules/gabarito/presentation/components/GabaritoDownloadLink';
import { useMyGabarito } from '../hooks/use-my-gabarito';
import { EmptyState, TabSkeleton } from './EmptyState';
import { InviteSection } from './InviteSection';

const OUTCOME_LABEL: Record<QuestionOutcome, string> = {
  right: 'acertou',
  wrong: 'errou',
  blank: 'em branco',
  annulled: 'anulada',
  pending: 'resposta oficial pendente',
};

/**
 * A aba Gabarito: as respostas da pessoa contra o gabarito oficial, questão a
 * questão, com a nota bruta no topo. É a grade do "Gabarito ENAMED" do
 * `enamed-feedback-lp`, com a resposta dela ao lado da oficial.
 *
 * O gabarito oficial vem da prova oficial e é publicado aos poucos: questão
 * sem resposta ainda aparece como pendente, e não entra na nota.
 *
 * "Baixar o gabarito" (o gabarito preliminar Aristo, em PDF) é a recompensa
 * do envio: **só aparece para quem já enviou**, à direita, logo acima do card
 * da grade (ou no topo, enquanto o gabarito oficial não sai). Antes, o
 * convite para enviar o anuncia.
 */
export function GabaritoTab() {
  const { exam, edition } = useTenant();
  const myGabarito = useMyGabarito();
  const submission = useGabaritoSubmission();
  const hasGabaritoMaterial = gabaritoMaterial(exam, edition) !== null;

  if (myGabarito.isPending) {
    return <TabSkeleton label="Carregando seu gabarito" />;
  }

  if (!myGabarito.answers) {
    return (
      <EmptyState
        title="Você ainda não enviou seu gabarito"
        note={
          submission !== 'open'
            ? GABARITO_SUBMISSION_NOTE[submission]
            : hasGabaritoMaterial
              ? 'Envie o seu gabarito para ter acesso ao gabarito preliminar Aristo.'
              : 'Envie o que você marcou na prova para ver sua correção, sua nota e sua posição no ranking.'
        }
      >
        <SendGabaritoAction variant="cta" local="painel" />
      </EmptyState>
    );
  }

  return (
    <GabaritoResult
      answers={myGabarito.answers}
      download={
        hasGabaritoMaterial && (
          <div className="ensaio-materials">
            <GabaritoDownloadLink />
          </div>
        )
      }
    />
  );
}

/** A correção de quem já enviou: nota bruta e grade contra o oficial. */
function GabaritoResult({
  answers,
  download,
}: {
  answers: Answers;
  /** A linha do "Baixar o gabarito", quando a edição tem o PDF. */
  download: ReactNode;
}) {
  const { edition } = useTenant();
  const officialExam = useOfficialExam();
  const recurso = edition.recursoQuestions ?? [];

  if (officialExam.isPending) {
    return <TabSkeleton label="Carregando o gabarito oficial" />;
  }

  if (!officialExam.answerKey) {
    return (
      <div className="stack-lg">
        {download}
        <EmptyState
          title="Gabarito oficial"
          note="O gabarito oficial ainda não foi publicado. Assim que sair, sua correção aparece aqui."
        />
      </div>
    );
  }

  const score = scoreGabarito(officialExam.answerKey, answers);

  return (
    <div className="stack-lg">
      <section className="card result-card">
        <p className="figure-label">
          Sua <span className="figure-label-accent">nota bruta</span>
        </p>
        <p className="figure figure-sm">
          {score.acertos}
          <span className="figure-of">/{score.validas}</span>
        </p>
        <p className="result-text result-center">
          {score.erros} {score.erros === 1 ? 'erro' : 'erros'} ·{' '}
          {score.emBranco} em branco
          {score.anuladas > 0 &&
            ` · ${score.anuladas} ${score.anuladas === 1 ? 'anulada' : 'anuladas'}`}
        </p>
        {score.pendentes > 0 && (
          <p className="result-footnote">
            {score.pendentes}{' '}
            {score.pendentes === 1
              ? 'questão ainda não tem'
              : 'questões ainda não têm'}{' '}
            resposta oficial publicada e não entram na nota. Ela muda quando
            forem publicadas.
          </p>
        )}
      </section>

      {/* Logo abaixo da nota, como no ENAMED: é ela que dá o motivo para
          chamar os colegas. */}
      <InviteSection />

      {download}
      <section className="card">
        <h3>Gabarito oficial {edition.year} × suas respostas</h3>
        <ul className="legend">
          <li>
            <span className="bar-swatch is-right" aria-hidden="true" />
            Acerto
          </li>
          <li>
            <span className="bar-swatch is-erro" aria-hidden="true" />
            Erro
          </li>
          <li>
            <span className="bar-swatch is-branco" aria-hidden="true" />
            Em branco
          </li>
          {recurso.length > 0 && (
            <li>
              <span className="bar-swatch is-recurso" aria-hidden="true" />
              Passível de recurso
            </li>
          )}
        </ul>
        <AnswerKeyGrid
          answerKey={officialExam.answerKey}
          answers={answers}
          recurso={recurso}
        />
        {recurso.length > 0 && (
          <p className="result-footnote">
            {recurso.length === 1 ? 'A questão' : 'As questões'}{' '}
            {formatList(recurso)}{' '}
            {recurso.length === 1 ? 'é passível' : 'são passíveis'} de recurso,
            na leitura da Aristo. A nota segue o gabarito oficial como está.
          </p>
        )}
      </section>
    </div>
  );
}

/** "78 e 99", "12, 78 e 99". */
function formatList(numbers: readonly number[]): string {
  if (numbers.length < 2) return numbers.join('');
  return `${numbers.slice(0, -1).join(', ')} e ${numbers.at(-1)}`;
}

function AnswerKeyGrid({
  answerKey,
  answers,
  recurso,
}: {
  answerKey: AnswerKeyEntry[];
  answers: Answers;
  /** Questões passíveis de recurso: só sinalizadas, a nota não muda. */
  recurso: readonly number[];
}) {
  return (
    <ol className="gkey">
      {answerKey.map((entry) => {
        const outcome = outcomeOf(entry, answers);
        const mine = answers[entry.number];
        const official = entry.isExcluded ? 'Anul.' : (entry.answer ?? '…');
        const isRecurso = recurso.includes(entry.number);

        return (
          <li
            key={entry.number}
            className={['gcell', `is-${outcome}`, isRecurso && 'is-recurso']
              .filter(Boolean)
              .join(' ')}
            aria-label={`Questão ${entry.number}: oficial ${official}, sua ${mine ?? 'em branco'}, ${OUTCOME_LABEL[outcome]}${isRecurso ? ', passível de recurso' : ''}`}
            title={isRecurso ? 'Passível de recurso' : undefined}
          >
            <span className="gcell-num">{entry.number}</span>
            <span className="gcell-answer">{official}</span>
            <span className="gcell-mine">{mine ?? '·'}</span>
          </li>
        );
      })}
    </ol>
  );
}
